import React, { useEffect, useState } from 'react'
import { Button, Switch, TextField, UserCheckValidateIcon, UserLockPrivacyShadedIcon } from '@diversioteam/diversio-ds'

import { usePublishPulseSurvey } from 'hooks/pulseSurveys/usePublishPulseSurvey'
import { useUpdatePulseSurvey } from 'hooks/pulseSurveys/useUpdatePulseSurvey'
import { usePulseSurveys } from 'hooks/usePulseSurveys'

import { PublishDialogProps } from './publishDialog.types'
import * as S from './publishDialog.styles'
import { getPublishErrorMessage, getPublishSummaryMessage } from './publishDialog.helper'

export const PublishDialog = (props: PublishDialogProps) => {
    const [isPrivate, setIsPrivate] = useState<boolean | null>(null)
    const [collectEmails, setCollectEmails] = useState(false)
    const [targetResponses, setTargetResponses] = useState(false)
    const [numberOfTargetResponses, setNumberOfTargetResponses] = useState<number | null>(null)
    const [isPublishing, setIsPublishing] = useState(false)
    const [countdown, setCountdown] = useState(5)

    const { activePulseSurvey } = usePulseSurveys()

    const { mutate: mutateUpdatePulseSurvey, isLoading: isLoadingUpdatePulseSurvey } = useUpdatePulseSurvey()
    const {
        mutate: mutatePublishPulseSurvey,
        isLoading: isLoadingPublishPulseSurvey,
        isError,
        error,
    } = usePublishPulseSurvey()

    const handlePrivacyChange = (privacy: boolean) => {
        setIsPrivate(privacy)
    }

    const handleCollectEmailsChange = () => {
        setCollectEmails(!collectEmails)
    }

    const handleTargetResponsesChange = () => {
        setTargetResponses(!targetResponses)
    }

    const handleNumberOfTargetResponsesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNumberOfTargetResponses(Number(event.target.value))
    }

    const handlePublishClick = () => {
        setIsPublishing(true)

        setCountdown(5)
    }

    const handleUndoClick = () => {
        setIsPublishing(false)
        setCountdown(5)
    }

    const handlePublishSuccess = () => {
        if (props.onClose) {
            props.onClose({}, 'backdropClick')
        }
    }

    const publishSurvey = () => {
        if (!activePulseSurvey) return

        mutatePublishPulseSurvey({ surveyUUID: activePulseSurvey.uuid }, { onSuccess: handlePublishSuccess })
    }

    const updateSurveyAndPublish = () => {
        if (!activePulseSurvey) return

        mutateUpdatePulseSurvey(
            {
                surveyUUID: activePulseSurvey.uuid,
                body: {
                    apply_anonymity: isPrivate || false,
                    ask_email: collectEmails,
                    target_respondent_count: numberOfTargetResponses || null,
                },
            },
            { onSuccess: publishSurvey },
        )
    }

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null

        if (isPublishing && countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prevCountdown) => prevCountdown - 1)
            }, 1000)
        } else if (isPublishing && countdown === 0) {
            setIsPublishing(false)
            updateSurveyAndPublish()
        }

        return () => {
            if (timer) {
                clearInterval(timer)
            }
        }
    }, [isPublishing, countdown])

    useEffect(() => {
        if (props.open) {
            setIsPrivate(null)
            setCollectEmails(false)
            setTargetResponses(false)
            setNumberOfTargetResponses(null)
        }
    }, [props.open])

    const isMutationLoading = isLoadingUpdatePulseSurvey || isLoadingPublishPulseSurvey

    return (
        <S.Dialog
            hideBackdrop={false}
            isLoading={isMutationLoading}
            {...props}
            open={isMutationLoading || isError ? true : props.open}
        >
            <>
                <S.Title>Publish survey</S.Title>

                <S.SectionWrapper isLoading={isMutationLoading}>
                    <S.Section>
                        <S.SectionTitle>How would you like to handle survey privacy for respondents?</S.SectionTitle>

                        <S.Required>* Select one of the following options</S.Required>

                        <S.PrivacyOptions>
                            <S.PrivacyOption selected={isPrivate === true} onClick={() => handlePrivacyChange(true)}>
                                <S.PrivacyOptionIcon>
                                    <UserLockPrivacyShadedIcon />
                                </S.PrivacyOptionIcon>

                                <S.PrivacyOptionTitle>Private</S.PrivacyOptionTitle>

                                <S.PrivacyOptionDescription>
                                    Respondents are informed that their employer <span>does not have access</span> to
                                    their raw data.
                                </S.PrivacyOptionDescription>

                                <S.PrivacyOptionList>
                                    <S.PrivacyOptionListItem>Typically higher response rate</S.PrivacyOptionListItem>
                                    <S.PrivacyOptionListItem>CSV download is not available</S.PrivacyOptionListItem>
                                </S.PrivacyOptionList>

                                <Button color="primary">Select Private</Button>
                            </S.PrivacyOption>

                            <S.PrivacyOption selected={isPrivate === false} onClick={() => handlePrivacyChange(false)}>
                                <S.PrivacyOptionIcon>
                                    <UserCheckValidateIcon />
                                </S.PrivacyOptionIcon>

                                <S.PrivacyOptionTitle>Not Private</S.PrivacyOptionTitle>

                                <S.PrivacyOptionDescription>
                                    Respondents are informed that their employer <span>has access</span> to their raw
                                    data.
                                </S.PrivacyOptionDescription>

                                <S.PrivacyOptionList>
                                    <S.PrivacyOptionListItem>Typically lower response rate</S.PrivacyOptionListItem>
                                    <S.PrivacyOptionListItem>CSV download is available</S.PrivacyOptionListItem>
                                </S.PrivacyOptionList>

                                <Button color="primary">Select Not Private</Button>
                            </S.PrivacyOption>
                        </S.PrivacyOptions>
                    </S.Section>

                    <S.Section>
                        <S.SectionTitle>Do you want to collect emails?</S.SectionTitle>

                        <S.SwitchWrapper>
                            <S.SwitchLabel>
                                {collectEmails ? 'Email question added!' : 'Add email question to your survey'}
                            </S.SwitchLabel>

                            <Switch checked={collectEmails} onChange={handleCollectEmailsChange} />
                        </S.SwitchWrapper>
                    </S.Section>

                    <S.Section>
                        <S.SectionTitle>Do you have a target number of responses?</S.SectionTitle>

                        <S.SwitchWrapper>
                            <S.SwitchLabel>
                                {targetResponses
                                    ? 'Define how many responses you expect for this survey. \nTurn this on to track progress towards a goal.'
                                    : 'You can define how many responses you expect for this survey. \nTurn this on to track progress towards a goal.'}
                            </S.SwitchLabel>

                            <Switch checked={targetResponses} onChange={handleTargetResponsesChange} />

                            {targetResponses && (
                                <S.InputWrapper>
                                    <S.InputLabel>Expected number of respondents</S.InputLabel>

                                    <TextField
                                        value={numberOfTargetResponses}
                                        onChange={handleNumberOfTargetResponsesChange}
                                        placeholder="Enter number"
                                        variant="filled"
                                        size="small"
                                        type="number"
                                        InputProps={{ inputProps: { min: 1 } }}
                                    />
                                </S.InputWrapper>
                            )}
                        </S.SwitchWrapper>
                    </S.Section>
                </S.SectionWrapper>

                {isError ? (
                    <S.ErrorSummary>{getPublishErrorMessage(error)}</S.ErrorSummary>
                ) : (
                    <S.Summary>{getPublishSummaryMessage(isLoadingPublishPulseSurvey)}</S.Summary>
                )}

                {isPublishing ? (
                    <S.Buttons>
                        <Button color="secondary" fullWidth onClick={handleUndoClick}>
                            Undo
                        </Button>

                        <Button color="primary" fullWidth>
                            Publishing in: {String(countdown).padStart(2, '0')}
                        </Button>
                    </S.Buttons>
                ) : (
                    <Button
                        color="primary"
                        fullWidth
                        disabled={isPrivate === null}
                        onClick={handlePublishClick}
                        loading={isLoadingUpdatePulseSurvey || isLoadingPublishPulseSurvey}
                    >
                        Publish
                    </Button>
                )}
            </>
        </S.Dialog>
    )
}
